// IrmaaCalculator — Medicare IRMAA (Income-Related Monthly Adjustment
// Amount) estimator for Part B + Part D premiums.
//
// Closes audit response §18 Phase 9 Medicare row "IRMAA calculator."
//
// IRMAA is computed from the beneficiary's MAGI (Modified Adjusted
// Gross Income) on the most recent IRS return — for 2026 IRMAA, that's
// the 2024 tax return. SSA uses a two-year lookback because the IRS
// data isn't current.
//
// IMPORTANT: bracket figures are published annually by CMS. The
// constants below are the 2025 brackets — operators must verify
// against the current CMS bulletin before quoting numbers to a
// beneficiary. The component prominently displays the source year +
// the "verify with SSA / Medicare" caveat.

import React from 'react';

// 2025 IRMAA brackets — applies to 2025 Part B + Part D premiums.
// Source: SSA POMS HI 01101.020 + CMS Annual Release Memo.
// MAGI thresholds are the LOWER bound INCLUSIVE; ranges roll up to
// just below the next bracket's lower bound.
// Verify before quoting: https://www.medicare.gov/your-medicare-costs/part-b-costs
const IRMAA_2025 = {
  source_year: 2025,
  magi_lookback_year: 2023,
  standard_part_b: 185.00,
  // Bracket 0 = standard premium, no IRMAA surcharge.
  // Each bracket: { magi_single_min, magi_married_min, part_b_surcharge, part_d_surcharge, label }
  brackets: [
    { idx: 0, magi_single_min:        0, magi_married_min:        0, part_b_surcharge:   0.00, part_d_surcharge:  0.00, label: 'Standard' },
    { idx: 1, magi_single_min:   106001, magi_married_min:   212001, part_b_surcharge:  74.00, part_d_surcharge: 13.70, label: 'Tier 1' },
    { idx: 2, magi_single_min:   133001, magi_married_min:   266001, part_b_surcharge: 185.00, part_d_surcharge: 35.30, label: 'Tier 2' },
    { idx: 3, magi_single_min:   167001, magi_married_min:   334001, part_b_surcharge: 295.90, part_d_surcharge: 57.00, label: 'Tier 3' },
    { idx: 4, magi_single_min:   200001, magi_married_min:   400001, part_b_surcharge: 406.90, part_d_surcharge: 78.60, label: 'Tier 4' },
    { idx: 5, magi_single_min:   500001, magi_married_min:   750001, part_b_surcharge: 443.90, part_d_surcharge: 85.80, label: 'Tier 5 (highest)' },
  ],
};

// MFS (Married Filing Separately) — different brackets, special case.
// 2025 MFS thresholds are flatter — only 3 tiers.
const IRMAA_2025_MFS = [
  { idx: 0, magi_min:        0, part_b_surcharge:   0.00, part_d_surcharge:  0.00, label: 'Standard' },
  { idx: 1, magi_min:   106001, part_b_surcharge: 406.90, part_d_surcharge: 78.60, label: 'MFS Tier 1' },
  { idx: 2, magi_min:   394001, part_b_surcharge: 443.90, part_d_surcharge: 85.80, label: 'MFS Tier 2' },
];

const FILING_STATUS_OPTIONS = [
  { key: 'single',  label: 'Single' },
  { key: 'married', label: 'Married filing jointly' },
  { key: 'mfs',     label: 'Married filing separately' },
];

function fmtCurrency(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

function findBracket(magi, status) {
  if (!Number.isFinite(magi) || magi < 0) return null;
  if (status === 'mfs') {
    return IRMAA_2025_MFS.slice().reverse().find(b => magi >= b.magi_min) || IRMAA_2025_MFS[0];
  }
  const isMarried = status === 'married';
  return IRMAA_2025.brackets.slice().reverse().find(b => {
    const threshold = isMarried ? b.magi_married_min : b.magi_single_min;
    return magi >= threshold;
  }) || IRMAA_2025.brackets[0];
}

export default function IrmaaCalculator({ onClose }) {
  const [magi, setMagi]               = React.useState('');
  const [filingStatus, setFilingStatus] = React.useState('single');
  const [hasPartD, setHasPartD]       = React.useState(true);

  const magiNum = Number(String(magi).replace(/[^\d.]/g, ''));
  const bracket = findBracket(magiNum, filingStatus);
  const isMagiValid = Number.isFinite(magiNum) && magiNum > 0;

  // Compute monthly + annual exposure.
  const partBPremium = IRMAA_2025.standard_part_b + (bracket?.part_b_surcharge || 0);
  const partDSurcharge = bracket?.part_d_surcharge || 0;
  const monthlyIrmaaTotal = (bracket?.part_b_surcharge || 0) + (hasPartD ? partDSurcharge : 0);
  const annualIrmaaTotal = monthlyIrmaaTotal * 12;

  // Per-spouse note for married filers (IRMAA applies per Medicare
  // beneficiary, so a married couple BOTH on Medicare pays IRMAA on
  // their respective premiums based on the same household MAGI).
  const isMarriedBoth = filingStatus === 'married';

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden max-w-none mx-auto">
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-blue-200/80">Medicare</div>
          <h2 className="text-xl font-bold">IRMAA calculator</h2>
          <p className="text-xs text-blue-100/80 mt-0.5">Estimate Part B + Part D income-related surcharges based on MAGI.</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-xs text-blue-100 hover:text-white bg-white/10 hover:bg-white/20 rounded px-3 py-1.5">
            Close
          </button>
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* Source caveat — required compliance text */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
          <strong>Source: {IRMAA_2025.source_year} CMS / SSA brackets</strong> — applies to {IRMAA_2025.source_year} premiums computed from {IRMAA_2025.magi_lookback_year} MAGI (2-year SSA lookback). <span className="italic">Verify against the current SSA bulletin before quoting a beneficiary.</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="text-slate-700 font-medium">MAGI ({IRMAA_2025.magi_lookback_year} tax return)</span>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={magi}
                onChange={e => setMagi(e.target.value)}
                placeholder="125,000"
                className="w-full pl-7 pr-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-violet-500 focus:outline-none"
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Adjusted Gross Income + tax-exempt interest. Line 11 of Form 1040 + Line 2a.</div>
          </label>

          <label className="text-sm">
            <span className="text-slate-700 font-medium">Filing status</span>
            <select
              value={filingStatus}
              onChange={e => setFilingStatus(e.target.value)}
              className="w-full mt-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-violet-500 focus:outline-none bg-white"
            >
              {FILING_STATUS_OPTIONS.map(o => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-1.5 mt-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={hasPartD}
                onChange={e => setHasPartD(e.target.checked)}
                className="rounded"
              />
              Include Part D / MAPD prescription coverage
            </label>
          </label>
        </div>

        {/* Result panel */}
        {!isMagiValid && (
          <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg p-5 text-center text-sm text-slate-500">
            Enter a MAGI above to see the IRMAA result.
          </div>
        )}

        {isMagiValid && bracket && (
          <div className={`border-2 rounded-lg p-5 ${
            bracket.idx === 0 ? 'bg-emerald-50 border-emerald-200' :
            bracket.idx >= 4  ? 'bg-rose-50 border-rose-200' :
                                'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{bracket.label}</div>
                <div className="text-2xl font-bold mt-0.5">
                  {bracket.idx === 0 ? 'No IRMAA surcharge' : fmtCurrency(monthlyIrmaaTotal) + '/mo extra'}
                </div>
                <div className="text-xs mt-1 opacity-80">
                  {bracket.idx === 0
                    ? `Beneficiary pays the standard Part B premium of ${fmtCurrency(IRMAA_2025.standard_part_b)}/mo.`
                    : `Annual IRMAA exposure: ${fmtCurrency(annualIrmaaTotal)}${isMarriedBoth ? ' per spouse on Medicare' : ''}.`}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4 text-xs">
              <div className="bg-white/80 rounded p-2">
                <div className="font-bold uppercase tracking-wider text-[10px] opacity-70">Part B premium</div>
                <div className="text-base font-bold tabular-nums">{fmtCurrency(partBPremium)}/mo</div>
                <div className="text-[10px] opacity-60">
                  {fmtCurrency(IRMAA_2025.standard_part_b)} standard
                  {bracket.part_b_surcharge > 0 && ` + ${fmtCurrency(bracket.part_b_surcharge)} IRMAA`}
                </div>
              </div>
              <div className="bg-white/80 rounded p-2">
                <div className="font-bold uppercase tracking-wider text-[10px] opacity-70">Part D surcharge</div>
                <div className="text-base font-bold tabular-nums">
                  {hasPartD ? fmtCurrency(partDSurcharge) + '/mo' : 'n/a'}
                </div>
                <div className="text-[10px] opacity-60">
                  {hasPartD
                    ? (partDSurcharge > 0 ? 'On TOP of the plan\'s base premium' : 'No surcharge at this MAGI')
                    : 'No Part D coverage selected'}
                </div>
              </div>
              <div className="bg-white/80 rounded p-2">
                <div className="font-bold uppercase tracking-wider text-[10px] opacity-70">Bracket idx</div>
                <div className="text-base font-bold tabular-nums">{bracket.idx} of 5</div>
                <div className="text-[10px] opacity-60">{IRMAA_2025.source_year} CMS</div>
              </div>
            </div>
          </div>
        )}

        {/* Full bracket reference — collapsed by default, helpful for explaining */}
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-600 font-medium hover:text-slate-900">
            Show full {IRMAA_2025.source_year} bracket table
          </summary>
          <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-2 py-1.5 text-left">Tier</th>
                  <th className="px-2 py-1.5 text-right">Single MAGI ≥</th>
                  <th className="px-2 py-1.5 text-right">Married MAGI ≥</th>
                  <th className="px-2 py-1.5 text-right">Part B surcharge</th>
                  <th className="px-2 py-1.5 text-right">Part D surcharge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {IRMAA_2025.brackets.map(b => (
                  <tr key={b.idx} className={bracket && b.idx === bracket.idx ? 'bg-violet-50 font-bold' : ''}>
                    <td className="px-2 py-1.5">{b.label}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{b.magi_single_min === 0 ? '—' : fmtCurrency(b.magi_single_min)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{b.magi_married_min === 0 ? '—' : fmtCurrency(b.magi_married_min)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{b.part_b_surcharge === 0 ? '—' : '+' + fmtCurrency(b.part_b_surcharge)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{b.part_d_surcharge === 0 ? '—' : '+' + fmtCurrency(b.part_d_surcharge)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-2 py-2 text-[10px] text-slate-500 leading-snug">
              MFS (Married Filing Separately) uses a flatter 3-tier schedule that jumps straight to high brackets. If filing status is MFS, the calculator above uses the MFS schedule automatically.
            </div>
          </div>
        </details>

        {/* Operator action footer */}
        <div className="text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
          <strong>Operator note:</strong> IRMAA appeals are filed with SSA Form SSA-44 when a life-changing event has reduced MAGI since the lookback year (retirement, work stoppage, loss of pension, divorce, death of spouse, etc.). Surface this option to any beneficiary in Tier 2+ — most don't know it exists.
        </div>
      </div>
    </div>
  );
}
